import React from "react";
import { CheckCircle2, Copy, Mail, Plus, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { useSchedule } from "@/lib/useSchedule";
import { hhmm, dayNames } from "@/lib/schedule";
import { AV } from "@/pages/availabilityText";

function ResponseEstimate() {
  const { lang } = useLang();
  const x = AV[lang] || AV.en;
  const s = useSchedule();
  if (!s.loaded) return null;
  const when = s.next ? `${dayNames(lang)[s.next.day]} ${hhmm(s.next.start)}` : "";
  const text = s.current.length ? x.estNow.replace("{names}", s.current.map((e) => e.name).join(", ")) : s.next ? x.estNext.replace("{when}", when) : x.estNone;
  return (
    <a href="/availability" data-testid="response-estimate" className="mx-auto mt-5 flex max-w-md items-start gap-3 rounded-lg border border-blue-500/25 bg-[#0b1430] p-4 text-left transition-colors hover:border-blue-400/50">
      <CalendarClock size={17} className="mt-0.5 shrink-0 text-[#FFCC00]" />
      <div>
        <div className="text-[12px] font-semibold uppercase tracking-wider text-blue-200">{x.estTitle}</div>
        <p className="mt-1 text-[13px] leading-relaxed text-zinc-300" data-testid="response-estimate-text">{text}</p>
      </div>
    </a>
  );
}

export function SuccessPanel({ result, onReset }) {
  const { tr } = useLang();

  const copy = () => {
    navigator.clipboard?.writeText(result.reference).catch(() => {});
    toast.success(tr.successRef, { description: result.reference });
  };

  return (
    <div className="animate-fade-up text-center" data-testid="success-panel">
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-emerald-500/15 text-emerald-400">
        <CheckCircle2 size={30} />
      </div>
      <h3 className="mt-5 text-xl font-semibold tracking-tight text-zinc-50">{tr.successTitle}</h3>

      <div className="mx-auto mt-6 max-w-sm rounded-lg border border-zinc-800 bg-zinc-950 p-5">
        <div className="label-caps">{tr.successRef}</div>
        <div className="mt-1 flex items-center justify-center gap-2">
          <span data-testid="success-reference" className="font-mono text-lg font-semibold text-blue-400">
            {result.reference}
          </span>
          <button onClick={copy} data-testid="copy-reference-btn" className="text-zinc-500 hover:text-zinc-200">
            <Copy size={15} />
          </button>
        </div>
      </div>

      <div className="mx-auto mt-4 flex max-w-md items-start justify-center gap-2 text-[13px] text-zinc-400">
        <Mail size={15} className="mt-0.5 shrink-0 text-zinc-500" />
        {result.anonymous ? (
          <span>{tr.successNoEmail}</span>
        ) : (
          <span>
            {tr.successEmail} <span className="text-zinc-200">{result.email}</span>
          </span>
        )}
      </div>
      <p className="mx-auto mt-3 max-w-md text-[13px] text-zinc-500">{tr.successNote}</p>
      <ResponseEstimate />

      <Button
        onClick={onReset}
        data-testid="new-report-btn"
        variant="outline"
        className="mt-6 h-10 border-zinc-800 bg-zinc-900 text-[13px] text-zinc-200 hover:bg-zinc-800"
      >
        <Plus size={15} /> {tr.newReport}
      </Button>
    </div>
  );
}
